import type {
  FeEmptyArea,
  FePerson,
  IdxToFeEmptyArea,
  QaItemKey,
} from "@/store/useCoverageBetaStore";
import type {
  CoverageBetaQaBoxKind,
  CoverageBetaQaReason,
  CoverageBetaQaResult,
} from "@/types/coverage";
import { values } from "lodash";

/**
 * A single reviewable item in Coverage Beta QA — an empty area or a person
 * box. `reportKind`/`reportIndex` are its wire-format identity (see
 * `CoverageBetaQaBoxVerdict`, types/coverage.ts) — computed once here so the
 * result builder and the admin-hydration reverse mapping both read the same
 * values instead of each re-deriving them. `reportIndex` is `null` only for a
 * deleted person: unrecoverable, it never appears in the parent submission,
 * so it has no wire identity to report.
 */
export type QaReviewItem = (
  | { kind: "area"; area: FeEmptyArea }
  | { kind: "person"; person: FePerson; personIdx: number }
) & {
  key: QaItemKey;
  isDeleted: boolean;
  reportKind: CoverageBetaQaBoxKind;
  reportIndex: number | null;
};

/**
 * Every reviewable item in a Coverage Beta QA task, across all four
 * categories the summary view groups boxes into (CoverageBetaSummaryView.tsx)
 * — Out of Stock Confirmed, Empty Area, Unblurred Persons, then Deleted
 * (areas, then persons). Object keys are small integers, so `values()`
 * already yields ascending index order within each group. Shared by the QA
 * result builder/hydrator below and the QA panel's pager/header progress, so
 * all three agree on what "item N of Total" means.
 */
export const getOrderedQaItems = (
  idxToFeEmptyArea: IdxToFeEmptyArea,
  persons: FePerson[],
): QaReviewItem[] => {
  const areas = values(idxToFeEmptyArea);
  const activeAreas = areas.filter((a) => !a.isDeleted);
  const deletedAreas = areas.filter((a) => a.isDeleted);
  const indexedPersons = persons.map((person, personIdx) => ({
    person,
    personIdx,
  }));
  const activePersons = indexedPersons.filter(
    ({ person }) => !person.isDeleted,
  );
  const deletedPersons = indexedPersons.filter(
    ({ person }) => person.isDeleted,
  );

  // Contiguous positions, assigned in encounter order — mirrors the parent
  // submission's own `product_selections`/`unblurred_persons` indexing.
  let areaPosition = 0;
  let personPosition = 0;

  const activeAreaItems: QaReviewItem[] = activeAreas.map((area) => ({
    key: `area:${area.feEmptyAreaIdx}`,
    kind: "area",
    area,
    isDeleted: false,
    reportKind:
      area.selectedProducts.length > 0 ? "oos_confirmed" : "empty_area",
    reportIndex: areaPosition++,
  }));

  const activePersonItems: QaReviewItem[] = activePersons.map(
    ({ person, personIdx }) => ({
      key: `person:${personIdx}`,
      kind: "person",
      person,
      personIdx,
      isDeleted: false,
      reportKind: "unblurred_person",
      reportIndex: personPosition++,
    }),
  );

  const deletedAreaItems: QaReviewItem[] = deletedAreas.map((area) => ({
    key: `area:${area.feEmptyAreaIdx}`,
    kind: "area",
    area,
    isDeleted: true,
    reportKind: "deleted",
    // Not a contiguous position — matches `deleted_empty_areas` entries,
    // which record the area's stable id (it was never in the submitted
    // `empty_areas` to begin with).
    reportIndex: area.feEmptyAreaIdx,
  }));

  const deletedPersonItems: QaReviewItem[] = deletedPersons.map(
    ({ person, personIdx }) => ({
      key: `person:${personIdx}`,
      kind: "person",
      person,
      personIdx,
      isDeleted: true,
      reportKind: "deleted",
      reportIndex: null,
    }),
  );

  return [
    ...activeAreaItems,
    ...activePersonItems,
    ...deletedAreaItems,
    ...deletedPersonItems,
  ];
};

/**
 * Whether a Coverage Beta QA task is ready to submit: the store is actually
 * hydrated from the parent submission, and every reviewable item carries a
 * verdict.
 *
 * The `isQaMode` half is load-bearing, not belt-and-braces. Only
 * CoverageBetaTask's QA hydration effect sets it (and its cleanup `reset()`
 * clears it), so it doubles as "the parent submission has loaded". An
 * unhydrated store has no areas and no persons, so `getOrderedQaItems`
 * returns `[]` and `[].every(...)` is vacuously `true` — without the
 * `isQaMode` check the submit button goes live over zero verdicts twice: on
 * first paint while the parcel and products load, and again in the window
 * after `reset()` between tasks. A click in either window would complete the
 * task with `boxes: []`, which reads downstream as a fully-reviewed,
 * nothing-flagged audit rather than as the no-op it actually is.
 *
 * A genuinely empty parent submission (no areas, no persons) still submits
 * fine — it's hydrated, so `isQaMode` is true and there's nothing to review.
 */
export const isQaReadyToSubmit = (state: {
  isQaMode: boolean;
  idxToFeEmptyArea: IdxToFeEmptyArea;
  persons: FePerson[];
  qaVerdicts: Record<QaItemKey, CoverageBetaQaReason[] | undefined>;
}): boolean => {
  if (!state.isQaMode) return false;
  return getOrderedQaItems(state.idxToFeEmptyArea, state.persons).every(
    (item) => state.qaVerdicts[item.key] !== undefined,
  );
};

/**
 * Build the Coverage Beta QA task-completion `result` payload. `qaVerdicts`
 * and `qaBoxNotes` are keyed by `QaItemKey`; this maps every reportable item
 * (skipping deleted persons — see `QaReviewItem`'s doc comment) onto its
 * wire-format verdict. Takes the store's own state shape, same as
 * `isQaReadyToSubmit` above, so its one caller can just hand over
 * `useCoverageBetaStore.getState()`.
 */
export const buildCoverageBetaQaResult = (state: {
  idxToFeEmptyArea: IdxToFeEmptyArea;
  persons: FePerson[];
  qaVerdicts: Record<QaItemKey, CoverageBetaQaReason[] | undefined>;
  qaBoxNotes: Record<QaItemKey, string>;
  qaTaskLevelCorrections: {
    unblurredPersonsMissed: boolean;
    boxesShouldHaveBeenDrawn: number;
    qaFeedback: string;
    flagLabeler: boolean;
  };
}): CoverageBetaQaResult => {
  const boxes: CoverageBetaQaResult["boxes"] = getOrderedQaItems(
    state.idxToFeEmptyArea,
    state.persons,
  )
    .filter((item) => item.reportIndex !== null)
    .map((item) => {
      const reasons = state.qaVerdicts[item.key] ?? [];
      return {
        kind: item.reportKind,
        index: item.reportIndex as number,
        qa_verdict: reasons.length === 0 ? "confirmed" : "incorrect",
        incorrect_reasons: reasons,
        // Trimmed, then emptied to null: the store holds "" for an untouched
        // textarea, but "no note" goes over the wire as one explicit value
        // (whitespace-only counts as blank).
        note: (state.qaBoxNotes[item.key] ?? "").trim() || null,
      };
    });

  const corrections = state.qaTaskLevelCorrections;
  return {
    boxes,
    unblurred_persons_missed: corrections.unblurredPersonsMissed,
    boxes_that_should_have_been_drawn: corrections.boxesShouldHaveBeenDrawn,
    // Same blank-to-null rule as a box's `note` above.
    qa_feedback: corrections.qaFeedback.trim() || null,
    flag_labeler: corrections.flagLabeler,
  };
};

/**
 * The reverse of `buildCoverageBetaQaResult`: zips a completed QA task's own
 * `result` back onto `QaItemKey`s, for preloading the read-only verdict
 * display when an admin browses an already-completed QA task. Takes the same
 * `items` list its caller already computed (e.g. to seed `activeQaItemKey`)
 * rather than recomputing it here. Returns both store slices the panel reads
 * per box, so a submitted note is visible on re-open rather than silently
 * dropped.
 */
export const mapCoverageBetaQaVerdicts = (
  items: QaReviewItem[],
  qaResult: CoverageBetaQaResult | undefined,
): {
  verdicts: Record<QaItemKey, CoverageBetaQaReason[] | undefined>;
  notes: Record<QaItemKey, string>;
} => {
  const verdicts: Record<QaItemKey, CoverageBetaQaReason[] | undefined> = {};
  const notes: Record<QaItemKey, string> = {};
  if (!qaResult) return { verdicts, notes };

  const boxByReportKey = new Map(
    (qaResult.boxes ?? []).map((b) => [`${b.kind}:${b.index}`, b] as const),
  );

  items.forEach((item) => {
    if (item.reportIndex === null) return;
    const box = boxByReportKey.get(`${item.reportKind}:${item.reportIndex}`);
    if (!box) return;
    if (box.incorrect_reasons) verdicts[item.key] = box.incorrect_reasons;
    if (box.note) notes[item.key] = box.note;
  });

  return { verdicts, notes };
};

/** Three-way "not reviewed / confirmed correct / flagged incorrect" styling for a QA-mode sidebar row — shared by SummaryRow and PersonSummaryRow. */
export const qaItemStatusStyle = (
  reasons: CoverageBetaQaReason[] | undefined,
): { borderClass: string; iconClass: string; title: string } =>
  reasons === undefined
    ? {
        borderClass: "border-l-4 border-gray-300",
        iconClass: "text-gray-400 opacity-40",
        title: "Not yet reviewed",
      }
    : reasons.length === 0
      ? {
          borderClass: "border-l-4 border-green-500",
          iconClass: "text-green-500",
          title: "Marked correct",
        }
      : {
          borderClass: "border-l-4 border-red-500",
          iconClass: "text-red-500",
          title: `Flagged incorrect (${reasons.length})`,
        };
